import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, MessageCircle, Send } from "lucide-react";
import { SOCIALS } from "@/components/Layout";
import useSeo from "@/hooks/useSeo";
import PageSeoNote from "@/components/PageSeoNote";
import { findKeywordConfig } from "@/constants/keywordLandings";

function fmtDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function CombinedShillongTable({ date, common }) {
  const cellClass =
    "py-3 text-slate-900 font-heading font-black text-lg text-center border-r border-slate-400 last:border-r-0";
  const headerClass =
    "py-2.5 bg-[#5cc9a7] text-slate-900 font-heading font-black text-lg text-center border-r border-slate-400 last:border-r-0";
  return (
    <div className="max-w-md mx-auto">
      {/* Date header */}
      <div className="text-center mb-2">
        <span className="font-heading font-black text-slate-900 text-2xl">{date}</span>
      </div>
      {/* Single bordered table */}
      <div className="ring-2 ring-slate-400 rounded-md overflow-hidden bg-white shadow">
        {/* SHILLONG spanning row */}
        <div className="bg-slate-200 text-center py-2 border-b border-slate-400">
          <span className="font-heading font-black text-slate-900 text-xl tracking-wide">
            SHILLONG
          </span>
        </div>
        {/* Row 1 header */}
        <div className="grid grid-cols-3 border-b border-slate-400">
          <div className={headerClass}>Direct</div>
          <div className={headerClass}>House</div>
          <div className={headerClass}>Ending</div>
        </div>
        {/* Row 1 values (F/R) */}
        <div className="grid grid-cols-3 border-b border-slate-400">
          <div className={cellClass + " break-words px-1"}>{common?.r1_direct || "—"}</div>
          <div className={cellClass}>{common?.r1_house || "—"}</div>
          <div className={cellClass}>{common?.r1_ending || "—"}</div>
        </div>
        {/* Row 2 header */}
        <div className="grid grid-cols-3 border-b border-slate-400">
          <div className={headerClass}>Direct</div>
          <div className={headerClass}>House</div>
          <div className={headerClass}>Ending</div>
        </div>
        {/* Row 2 values (S/R) */}
        <div className="grid grid-cols-3">
          <div className={cellClass + " break-words px-1"}>{common?.r2_direct || "—"}</div>
          <div className={cellClass}>{common?.r2_house || "—"}</div>
          <div className={cellClass}>{common?.r2_ending || "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function CommonNumber() {
  const { pathname } = useLocation();
  const kw = findKeywordConfig(pathname);
  useSeo({
    title: kw?.title || "Morning Teer Common Number Today | House and Ending",
    description:
      kw?.description ||
      "Morning Teer Common Number today — direct, house and ending picks for FR and SR of the Shillong Morning Teer draw. Study reference only.",
    path: pathname,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Common Number", path: pathname },
    ],
  });

  const [date, setDate] = useState("");
  const [common, setCommon] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/common-numbers/latest`)
      .then((r) => {
        const c = r.data.common;
        setCommon(c);
        setDate(c?.date || "");
      })
      .catch(() => {
        setCommon(null);
        setDate("");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="max-w-xl mx-auto w-full px-4 pt-6 pb-10">
        {/* Hidden H1 for SEO */}
        <h1 className="sr-only">{kw?.h1 || "Morning Teer Common Number"}</h1>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading…</div>
        ) : !common ? (
          <>
            <Card className="p-8 text-center border-dashed border-slate-300 max-w-md mx-auto">
              <div className="text-slate-500 text-sm">
                No common numbers published yet.
              </div>
            </Card>
          </>
        ) : (
          <CombinedShillongTable date={fmtDMY(date)} common={common} />
        )}

        {/* Disclaimer */}
        <div className="mt-8 flex items-start gap-3 px-2">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <span className="font-bold">Disclaimer:</span> These common numbers are purely based on
            certain calculations done using past results. There is no guarantee of the accuracy of
            these numbers.
          </p>
        </div>

        {/* Channels — right below disclaimer per user request */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={SOCIALS.whatsappChannel}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cn-whatsapp-channel"
            className="flex items-center gap-3 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-3 hover:bg-emerald-100 transition"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <MessageCircle className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                Join
              </div>
              <div className="text-sm font-heading font-bold text-slate-900 truncate">
                WhatsApp Channel
              </div>
            </div>
          </a>
          <a
            href={SOCIALS.telegram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cn-telegram-channel"
            className="flex items-center gap-3 rounded-2xl bg-sky-50 ring-1 ring-sky-200 px-4 py-3 hover:bg-sky-100 transition"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500 text-white">
              <Send className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">
                Join
              </div>
              <div className="text-sm font-heading font-bold text-slate-900 truncate">
                Telegram Channel
              </div>
            </div>
          </a>
        </div>
      </div>
      <PageSeoNote topic="common-number" />
    </>
  );
}
